import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Card, Button, Row, Col, ListGroup, Spinner, Badge, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { getMeasurementUnitDetails, deleteMeasurementUnit } from '../../Controller/MeasurementUnitController';
import ConfirmationAlert from '../../../../../GlobalModules/Utility/Components/ConfirmationAlert';
import Utility from '../../../../../GlobalModules/Utility/Utility';
import Modal from 'react-bootstrap/Modal';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';

/**
 * Component for viewing measurement unit details
 * @returns {JSX.Element}
 */
const MeasurementUnitView = (props) => {
  const { lng, id } = useParams();
  const history = useHistory();

  const [measurementUnit, setMeasurementUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch measurement unit details on component mount
  useEffect(() => {
    if (id) {
      fetchMeasurementUnitDetails();
    }
  }, [id]);

  /**
   * Fetch measurement unit details
   */
  const fetchMeasurementUnitDetails = async () => {
    setLoading(true);
    try {
      const response = await getMeasurementUnitDetails(id);

      if (response.success) {
        setMeasurementUnit(response.data);
      } else {
        Utility.toastNotifications(response.message || 'Failed to fetch measurement unit details', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error fetching measurement unit details:', error);
      Utility.toastNotifications('An error occurred while fetching measurement unit details', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open delete confirmation modal
   */
  const confirmDelete = () => {
    setShowDeleteModal(true);
  };

  /**
   * Close delete confirmation modal
   */
  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  /**
   * Handle delete action
   */
  const handleDelete = async () => {
    try {
      setLoading(true);

      const response = await deleteMeasurementUnit(id);

      if (response.success) {
        Utility.toastNotifications(response.message || 'Measurement unit deleted successfully', 'Success', 'success');
        // Navigate back to list page
        history.push(`/${lng}/settings/measurement-units`);
      } else {
        Utility.toastNotifications(response.message || 'Failed to delete measurement unit', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error deleting measurement unit:', error);
      Utility.toastNotifications('An error occurred while deleting the measurement unit', 'Error', 'error');
    } finally {
      setShowDeleteModal(false);
      setLoading(false);
    }
  };

  /**
   * Handle back button click
   */
  const handleBack = () => {
    history.push(`/${lng}/settings/measurement-units`);
  };

  //======= add action permission ====//
  // Check multiple permissions at once
  const measurementUnitViewPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'measurement_units',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  return (
    <Container fluid>
      <div className="page-header">
        <div className="page-title">
          <h4>View Measurement Unit</h4>
          <h6>View measurement unit details</h6>
        </div>
        <div className="page-btn">
          <Button variant="secondary" onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to List
          </Button>
        </div>
      </div>

      <Row>
        <Col md={12}>
          <Card>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  {/* <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading measurement unit details...</p> */}
                </div>
              ) : measurementUnit ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0">{measurementUnit.label}</h5>
                    <div>
                      {measurementUnitViewPermissions.edit && (
                        <Link to={`/${lng}/settings/measurement-units/edit/${id}`} className="btn btn-sm btn-primary me-2">
                          <FontAwesomeIcon icon={faEdit} className="me-1" /> Edit
                        </Link>
                      )}
                      {measurementUnitViewPermissions.delete && (
                        <Button variant="danger" size="sm" onClick={confirmDelete}>
                          <FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>

                  <ListGroup>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Label:</Col>
                        <Col md={8}>{measurementUnit.label || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Unit Code:</Col>
                        <Col md={8}>{measurementUnit.unit_code || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Status:</Col>
                        <Col md={8}>
                          <Badge bg={measurementUnit.status === 1 ? 'success' : 'danger'}>
                            {measurementUnit.status === 1 ? 'Active' : 'Inactive'}
                          </Badge>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Created On:</Col>
                        <Col md={8}>{Utility.get12HourFormat(measurementUnit.createdAt) || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Last Updated:</Col>
                        <Col md={8}>{Utility.get12HourFormat(measurementUnit.updatedAt) || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Created By:</Col>
                        <Col md={8}>{measurementUnit.created_by_user || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                  </ListGroup>
                </>
              ) : (
                <div className="text-center py-5">
                  <p>Measurement unit not found or has been deleted.</p>
                  <Button variant="primary" onClick={handleBack}>
                    Back to List
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ConfirmationAlert
            BodyFirstContent="Are you sure you want to delete measurement unit:"
            BodySecondContent={measurementUnit?.name}
            BodyThirdContent="?"
            BodyForthContent="This action cannot be undone."
            confirmationButtonContent="Yes, delete it!"
            cancelButtonContent="Cancel"
            deleteIconVissable={true}
            deleteConfirmButton={handleDelete}
            deleteCancleButton={cancelDelete}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token
  };
}

export default connect(mapStateToProps)(withNamespaces()(MeasurementUnitView));

